import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Badge,
  useScrollTrigger,
  Slide,
  MenuItem,
  Paper,
  ClickAwayListener,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingBagOutlined as CartIcon,
  PersonOutline as AccountIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  DarkModeOutlined,
  LightModeOutlined
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useState as useColorModeState } from 'react';

import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
import logo from '../../assets/ar-infotech-solutions.png';
import { products as allProducts } from '../../config/products';
import { getProductImage } from '../../utils/imageHelper';

const DesktopDropdownItem = ({ item, navigate }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{ position: 'relative' }}
    >
      <MenuItem
        onClick={() => navigate(item.path)}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          '&:hover': { backgroundColor: '#F9FAF4', color: '#0e6ada' },
          py: 1.5,
          px: 2
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.label}</Typography>
        {item.subDropdown && <KeyboardArrowRight fontSize="small" sx={{ ml: 2 }} />}
      </MenuItem>

      {item.subDropdown && open && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 0,
            left: '100%',
            minWidth: '200px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            py: 1,
            ml: 0.5,
            zIndex: 1300
          }}
        >
          {item.subDropdown.map((subItem) => (
            <MenuItem
              key={subItem.label}
              onClick={() => {
                navigate(subItem.path);
                setOpen(false); // Close on click
              }}
              sx={{ '&:hover': { backgroundColor: '#F9FAF4', color: '#0e6ada' }, py: 1.5, px: 2 }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{subItem.label}</Typography>
            </MenuItem>
          ))}
        </Paper>
      )}
    </Box>
  );
};

const DesktopNavItem = ({ item, navigate }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
    >
      <Button
        onClick={() => navigate(item.path)}
        endIcon={item.dropdown && <KeyboardArrowDown />}
        sx={{
          color: 'text.primary',
          fontWeight: 700,
          fontSize: '1rem',
          '&:hover': { color: '#0e6ada' },
          textTransform: 'capitalize',
          minWidth: 0,
          py: 2 // Make hover target larger
        }}
      >
        {item.label}
      </Button>

      {item.dropdown && open && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '240px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            py: 1,
            mt: -1, // Overlap slightly to prevent hover gap issues
            zIndex: 1200
          }}
        >
          {item.dropdown.map((dropItem) => (
            <DesktopDropdownItem key={dropItem.label} item={dropItem} navigate={navigate} />
          ))}
        </Paper>
      )}
    </Box>
  );
};

const MobileDropdownItem = ({ item, handleDrawerToggle, navigate }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    if (item.subDropdown) {
      e.stopPropagation();
      setOpen(!open);
    } else {
      navigate(item.path);
      handleDrawerToggle();
    }
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItem
          button
          onClick={handleClick}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: '8px',
            mb: 0.5,
            '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
          />
          {item.subDropdown && (open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />)}
        </ListItem>
      </ListItem>

      {item.subDropdown && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 3 }}>
            {item.subDropdown.map((subItem) => (
              <ListItem disablePadding key={subItem.label}>
                <ListItem
                  button
                  onClick={() => { navigate(subItem.path); handleDrawerToggle(); }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: '8px',
                    mb: 0.5,
                    '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
                  }}
                >
                  <ListItemText
                    primary={subItem.label}
                    primaryTypographyProps={{ fontWeight: 400, fontSize: '0.9rem' }}
                  />
                </ListItem>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const MobileNavItem = ({ item, handleDrawerToggle, navigate }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    if (item.dropdown) {
      e.stopPropagation();
      setOpen(!open);
    } else {
      navigate(item.path);
      handleDrawerToggle();
    }
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItem
          button
          onClick={handleClick}
          sx={{
            textAlign: 'left',
            py: 1.5,
            px: 2,
            borderRadius: '8px',
            mb: 0.5,
            '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}
          />
          {item.dropdown && (open ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}
        </ListItem>
      </ListItem>

      {item.dropdown && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {item.dropdown.map((dropItem) => (
              <MobileDropdownItem key={dropItem.label} item={dropItem} handleDrawerToggle={handleDrawerToggle} navigate={navigate} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [darkMode, setDarkMode] = useColorModeState(() => {
    return localStorage.getItem('arInfotechDarkMode') === 'true';
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { totalItems } = useCart();
  const { user } = useUser();

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 1) {
      const filtered = allProducts.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      navigate(`/search?q=${searchQuery}`);
      setShowSuggestions(false);
    }
  };

  const handleDarkModeToggle = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('arInfotechDarkMode', next);
    document.body.style.backgroundColor = next ? '#121212' : '#ffffff';
    document.body.style.color = next ? '#ffffff' : '#000000';
  };

  // Updated Navigation Data Structure
  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'Microsoft',
      path: '/shop?category=microsoft',
      dropdown: [
        {
          label: 'Windows',
          path: '/shop?category=windows',
          subDropdown: [
            { label: 'Windows 7', path: '/shop?category=windows-7' },
            { label: 'Windows 8', path: '/shop?category=windows-8' },
            { label: 'Windows 8.1', path: '/shop?category=windows-8-1' },
            { label: 'Windows 10', path: '/shop?category=windows-10' },
            { label: 'Windows 11', path: '/shop?category=windows-11' },
          ]
        },
        { label: 'MS Office', path: '/shop?category=ms-office' },
        { label: 'Servers', path: '/shop?category=servers' },
        { label: 'SQL Server', path: '/shop?category=sql-server' },
        { label: 'Visual Studio', path: '/shop?category=visual-studio' },
      ]
    },
    { label: 'Adobe', path: '/shop?category=adobe' },
    {
      label: 'Others',
      path: '/shop?category=others',
      dropdown: [
        { label: 'Antivirus', path: '/shop?category=antivirus' },
        { label: 'Auto Desk', path: '/shop?category=auto-desk' },
        { label: 'CorelDraw', path: '/shop?category=coreldraw' },
        { label: 'Tally', path: '/shop?category=tally' },
      ]
    },
    { label: 'Combo', path: '/shop?category=combo' },
    { label: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: isMobile ? 1 : 2, px: 1 }}>
        <Box component="img" src={logo} alt="AR Infotech" sx={{ height: "45px", width: "auto" }} />
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <MobileNavItem key={item.label} item={item} handleDrawerToggle={handleDrawerToggle} navigate={navigate} />
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1100, backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #EAEAEA' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '64px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ display: { md: 'none' }, mr: isMobile ? 0 : 0.5 }}>
                <MenuIcon />
              </IconButton>

              <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <Box component="img" src={logo} alt="AR Infotech" sx={{ height: isMobile ? "40px" : "80px", width: "auto" }} />
              </Box>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center', height: '100%' }}>
              {navItems.map((item) => (
                <DesktopNavItem key={item.label} item={item} navigate={navigate} />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ position: 'relative', display: { xs: 'none', lg: 'block' } }}>
                <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#F5F5F5', px: 2, borderRadius: 1 }}>
                      <input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchSubmit}
                        style={{ border: 'none', background: 'transparent', padding: '10px', width: '200px', outline: 'none' }}
                      />
                      <SearchIcon fontSize="small" color="action" />
                    </Box>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                      <Paper
                        elevation={4}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          mt: 1,
                          zIndex: 1200,
                          maxHeight: 300,
                          overflowY: 'auto',
                          borderRadius: 2
                        }}
                      >
                        <List disablePadding>
                          {suggestions.map((prod) => (
                            <React.Fragment key={prod.id}>
                              <ListItem
                                button
                                onClick={() => {
                                  navigate(`/product/${prod.slug}`);
                                  setShowSuggestions(false);
                                  setSearchQuery('');
                                }}
                                sx={{ py: 1.5 }}
                              >
                                <Box component="img" src={getProductImage(prod.image)} sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, p: 0.5, bgcolor: '#FFF', borderRadius: 1 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{prod.name}</Typography>
                                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>₹{prod.price}</Typography>
                                </Box>
                              </ListItem>
                              <Divider component="li" />
                            </React.Fragment>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                </ClickAwayListener>
              </Box>
              {/* <IconButton
                color="inherit"
                onClick={handleDarkModeToggle}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{ color: darkMode ? '#FFD54F' : 'text.primary' }}
              >
                {darkMode ? <LightModeOutlined sx={{ fontSize: 26 }} /> : <DarkModeOutlined sx={{ fontSize: 26 }} />}
              </IconButton> */}
              <IconButton
                color="inherit"
                onClick={() => navigate('/cart')}
                aria-label={`Shopping cart, ${totalItems} items`}
                sx={{
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.08)' }
                }}
              >
                <Badge
                  badgeContent={totalItems}
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontWeight: 700,
                      backgroundColor: '#0E6ADA',
                      color: '#FFF'
                    }
                  }}
                >
                  <CartIcon sx={{ fontSize: 28 }} />
                </Badge>
              </IconButton>
              <IconButton color="inherit" onClick={() => navigate(user ? '/account' : '/login')} aria-label={user ? 'My account' : 'Sign in'}>
                <AccountIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </Box>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 300 } }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
